import { useDispatch, useSelector } from "react-redux";
import { Field, FieldArray, Form, Formik, setIn } from "formik";
import {
  buildItemGroupCompleteTouched,
  firstFormikErrorMessage,
  itemGroupSchema,
} from "../../utils/validation.util.js";
import instance from "../../utils/api_instance.util.js";
import { errorToastify, successToastify } from "../../utils/toastify.util.js";
import { resolveUploadUrl } from "../../utils/upload.util.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Button from "../../components/button/Button.jsx";
import IapImageCropperModal from "../../components/image/IapImageCropperModal.jsx";
import {
  fetchGroupDetails,
  updateItemGroup,
  updateItemOrder,
} from "../../features/itemSlice.js";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAllCategories } from "../../features/categorySlice.js";
import { fetchSubCategoryDataByCatagoryId } from "../../features/subCategorySlice.js";
import DraggableItemCard from "./DraggableItemCard.jsx";
import { InputError, LevelReq } from "../../components/form/FormBody.jsx";
import { getUserDetails } from "../../features/userSlice.js";

/** ✅ Multi-select dropdown component */
const MultiSelectDropdown = ({
  label,
  placeholder = "Search...",
  options = [],
  selectedIds = [],
  onToggle,
  onDone,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedText = useMemo(() => {
    const count = selectedIds.length || 0;
    return count ? `${count} Selected` : `Select ${label}`;
  }, [selectedIds, label]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o?.name || o?.title || "")
        .toLowerCase()
        .includes(q),
    );
  }, [options, search]);

  const wrapRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="col-md-6 mb-3" ref={wrapRef}>
      <label className="form-label fs-7">{label}</label>

      <div className="category-dd">
        <div
          className={`form-control category-dd-input ${disabled ? "bg-light" : ""}`}
          role="button"
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
          style={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span>{selectedText}</span>
          <span className="category-dd-arrow">{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div className="category-dd-panel">
            <input
              type="text"
              className="form-control mb-2"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="category-dd-list">
              {filtered.length === 0 ? (
                <div className="text-muted small">No data found</div>
              ) : (
                <div className="category-dd-grid">
                  {filtered.map((item) => (
                    <label key={item.id} className="category-dd-item">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(Number(item.id))}
                        onChange={() => onToggle(Number(item.id))}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="category-dd-footer">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setOpen(false);
                  onDone && onDone();
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const itemGroupData = {
  id: "",
  name: "",
  description: "",
  category: "",
  subCategory: "",
  price: "",
  logo: "",
  bannerImg: "",
  iapimage: "",
  status: "draft",
  isDraft: true,
  categoryIds: [],
  subCategoryIds: [],
  items: [
    {
      item_name: "",
      item_desc: "",
      item_img: "",
      position: 1,
    },
  ],
};

const ItemGroupUpdatePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const { isBtnLoad, groupData, isUpdate } = useSelector((state) => state.item);
  const { allCategory } = useSelector((state) => state.category);
  const { allSubCatagories } = useSelector((state) => state.subcategory);

  const [logo, setLogo] = useState("");
  const [bannerImg, setBannerImg] = useState("");
  const [packageData, setPackageData] = useState(itemGroupData);
  const [deleteItems, setDeleteItems] = useState([]);

  const [subPool, setSubPool] = useState([]);
  const [iapImage, setIapImage] = useState("");
  const [logoCropState, setLogoCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "logo-image.jpg",
  });
  const [bannerCropState, setBannerCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "banner-image.jpg",
  });
  const [iapCropState, setIapCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "iap-image.jpg",
  });
  const [itemCropState, setItemCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "item-image.jpg",
    itemIndex: null,
  });
  const [submitMode, setSubmitMode] = useState("complete");
  const logoFieldSetterRef = useRef(null);
  const bannerFieldSetterRef = useRef(null);
  const iapFieldSetterRef = useRef(null);
  const itemFieldSetterRef = useRef(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const iapInputRef = useRef(null);
  const user = useSelector((state) => state.user.userDetails);
  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  const orderTimerRef = useRef(null);

  useEffect(() => {
    if (!id) {
      navigate("/items");
      return;
    }
    dispatch(getAllCategories());
    dispatch(fetchGroupDetails({ groupId: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUpdate) {
      navigate("/items");
    }
  }, [isUpdate, navigate]);

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const buildCroppedFileName = (originalName, prefix) => {
    const safePrefix = prefix || "cropped-image";
    const sourceName = String(originalName || "").trim();
    const baseName = sourceName.replace(/\.[^/.]+$/, "") || safePrefix;
    const normalizedBase = baseName
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    return `${normalizedBase || safePrefix}-${Date.now()}.jpg`;
  };

  const fetchAndMergeSubcategories = async (categoryIds) => {
    try {
      const results = await Promise.all(
        categoryIds.map((cid) =>
          dispatch(fetchSubCategoryDataByCatagoryId({ category_id: cid })),
        ),
      );

      let merged = [];
      results.forEach((r) => {
        const payload = r?.payload;
        const list =
          payload?.result?.list ||
          payload?.result?.data ||
          payload?.result ||
          payload?.data ||
          payload ||
          [];

        if (Array.isArray(list)) merged = merged.concat(list);
      });

      // fallback: include store list too
      if (Array.isArray(allSubCatagories) && allSubCatagories.length) {
        merged = merged.concat(allSubCatagories);
      }

      const map = new Map();
      merged.forEach((s) => {
        const sid = Number(s?.id);
        if (!sid) return;
        if (!map.has(sid))
          map.set(sid, { id: sid, name: s?.name || `Sub ${sid}` });
      });

      setSubPool(Array.from(map.values()));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!groupData) return;

    const tempItems = (groupData.items || []).map((item) => ({
      id: item.id,
      group_id: item.group_id,
      item_name: item.item_name,
      item_desc: item.item_description,
      item_img: item.item_iamge_url,
      position: item.position || 0,
    }));

    setLogo(groupData.group_image_url || "");
    setBannerImg(groupData.banner_img_url || "");
    setIapImage(groupData.iapimage || "");

    const categoryIds = Array.isArray(groupData.categoryIds)
      ? groupData.categoryIds.map(Number)
      : groupData.category
        ? [Number(groupData.category)]
        : [];

    const subCategoryIds = Array.isArray(groupData.subCategoryIds)
      ? groupData.subCategoryIds.map(Number)
      : groupData.subCategory
        ? [Number(groupData.subCategory)]
        : [];

    const incomingStatus =
      groupData.status || (groupData.isDraft ? "draft" : "complete");
    const isDraftInitial = incomingStatus === "draft";

    setPackageData({
      id: groupData.id,
      name: groupData.group_name,
      description: groupData.group_description,
      price: groupData.price,
      logo: groupData.group_image_url,
      bannerImg: groupData.banner_img_url,
      categoryIds,
      subCategoryIds,
      category: categoryIds[0] ? String(categoryIds[0]) : "",
      subCategory: subCategoryIds[0] ? String(subCategoryIds[0]) : "",
      items: tempItems,
      iapimage: groupData.iapimage,
      status: incomingStatus,
      isDraft: isDraftInitial,
    });

    setSubmitMode(isDraftInitial ? "draft" : "complete");

    if (categoryIds.length) {
      fetchAndMergeSubcategories(categoryIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData]);

  const categoryOptions = useMemo(() => {
    return Array.isArray(allCategory)
      ? allCategory.map((c) => ({ id: c.id, name: c.name }))
      : [];
  }, [allCategory]);

  const subCategoryOptions = useMemo(() => {
    if (Array.isArray(subPool) && subPool.length) return subPool;

    if (Array.isArray(allSubCatagories) && allSubCatagories.length) {
      const map = new Map();
      allSubCatagories.forEach((s) => {
        const sid = Number(s?.id);
        if (!sid) return;
        if (!map.has(sid))
          map.set(sid, { id: sid, name: s?.name || `Sub ${sid}` });
      });
      return Array.from(map.values());
    }
    return [];
  }, [subPool, allSubCatagories]);

  const buildSubOptionsWithSelected = (options, selectedIds) => {
    const map = new Map((options || []).map((x) => [Number(x.id), x]));
    (selectedIds || []).forEach((sid) => {
      const id = Number(sid);
      if (!id) return;
      if (!map.has(id)) map.set(id, { id, name: `Selected (${id})` });
    });
    return Array.from(map.values());
  };

  const onSubmitHandler = (values, mode) => {
    const isDraft = mode === "draft";
    values.status = isDraft ? "draft" : "complete";
    values.isDraft = isDraft;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("ItemGroupUpdate submit", { status: values?.status, name: values?.name });
    }

    if (!isDraft) {
      if (!values.logo || !isValidUrl(values.logo)) {
        errorToastify("Challenge pack logo required");
        return false;
      }
      if (!values.bannerImg || !isValidUrl(values.bannerImg)) {
        errorToastify("Challenge pack banner image required");
        return false;
      }
      if (!values.iapimage || !isValidUrl(values.iapimage)) {
        errorToastify("IAP image required");
        return false;
      }
      if (!Array.isArray(values.items) || values.items.length === 0) {
        errorToastify("Challenges array is missing or empty.");
        return false;
      }
      for (let i = 0; i < values.items.length; i++) {
        const item = values.items[i];
        if (!item.item_img || !isValidUrl(item.item_img)) {
          errorToastify(`Challenge image required on challenge ${i + 1}`);
          return false;
        }
      }
    }

    const categoryIds = Array.isArray(values.categoryIds)
      ? values.categoryIds
      : [];
    const subCategoryIds = Array.isArray(values.subCategoryIds)
      ? values.subCategoryIds
      : [];

    if (!isDraft) {
      if (!categoryIds.length) {
        errorToastify("Please select at least one category");
        return false;
      }
      if (!subCategoryIds.length) {
        errorToastify("Please select at least one sub-category");
        return false;
      }
    }

    const payload = { ...values };

    payload.deleteItems = deleteItems;
    payload.categoryIds = categoryIds.map(Number);
    payload.subCategoryIds = subCategoryIds.map(Number);

    payload.category = payload.categoryIds[0]
      ? String(payload.categoryIds[0])
      : "";
    payload.subCategory = payload.subCategoryIds[0]
      ? String(payload.subCategoryIds[0])
      : "";

    dispatch(updateItemGroup(payload));
  };

  const closeLogoCropper = () => {
    if (logoCropState.imageSrc) {
      URL.revokeObjectURL(logoCropState.imageSrc);
    }
    setLogoCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "logo-image.jpg",
    });
  };

  const fileUploadLogo = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    logoFieldSetterRef.current = setFieldValue;
    setLogoCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "logo-image.jpg",
    });
    event.target.value = "";
  };

  const uploadLogoImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(logoCropState.fileName, "logo-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);
      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);

      if (data.success) {
        successToastify(data.message || "Uploaded");
        logoFieldSetterRef.current?.("logo", data.result.url);
        setLogo(data.result.url);
        closeLogoCropper();
      }
    } catch (err) {
      errorToastify(err?.response?.data?.message || "Logo upload failed");
    }
  };

  const closeBannerCropper = () => {
    if (bannerCropState.imageSrc) {
      URL.revokeObjectURL(bannerCropState.imageSrc);
    }
    setBannerCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "banner-image.jpg",
    });
  };

  const fileUploadBanner = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    bannerFieldSetterRef.current = setFieldValue;
    setBannerCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "banner-image.jpg",
    });
    event.target.value = "";
  };

  const uploadBannerImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(bannerCropState.fileName, "banner-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);
      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);

      if (data.success) {
        successToastify(data.message || "Uploaded");
        bannerFieldSetterRef.current?.("bannerImg", data.result.url);
        setBannerImg(data.result.url);
        closeBannerCropper();
      }
    } catch (err) {
      errorToastify(err?.response?.data?.message || "Banner upload failed");
    }
  };

  const closeItemCropper = () => {
    if (itemCropState.imageSrc) {
      URL.revokeObjectURL(itemCropState.imageSrc);
    }
    setItemCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "item-image.jpg",
      itemIndex: null,
    });
  };

  const uploadItemImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(itemCropState.fileName, "item-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);

      const { data } = await instance.post(
        `/api/v1/common/file/upload`,
        fileData,
      );
      if (data.success) {
        successToastify(data.message || "Uploaded");
        const itemIndex = itemCropState.itemIndex;
        if (itemIndex !== null) {
          itemFieldSetterRef.current?.(
            `items.${itemIndex}.item_img`,
            data.result.url,
          );
        }
        closeItemCropper();
      }
    } catch (err) {
      errorToastify(err?.response?.data?.message || "Challenge upload failed");
    }
  };

  const fileUploadItem = (event, index, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    itemFieldSetterRef.current = setFieldValue;
    setItemCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "item-image.jpg",
      itemIndex: index,
    });
    event.target.value = "";
  };

  const closeIapCropper = () => {
    if (iapCropState.imageSrc) {
      URL.revokeObjectURL(iapCropState.imageSrc);
    }
    setIapCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "iap-image.jpg",
    });
  };

  const uploadIapImageBlob = async (blob) => {
    try {
      const fileData = new FormData();
      const uploadFile = new File(
        [blob],
        buildCroppedFileName(iapCropState.fileName, "iap-image"),
        { type: "image/jpeg" },
      );
      fileData.append("file", uploadFile);

      const { data } = await instance.post(`/api/v1/common/file/upload`, fileData);

      const uploadedUrl = resolveUploadUrl(data);
      if (data.success && uploadedUrl) {
        successToastify(data.message || "Uploaded");
        iapFieldSetterRef.current?.("iapimage", uploadedUrl);
        setIapImage(uploadedUrl);
        closeIapCropper();
      } else if (data.success) {
        errorToastify("Upload succeeded but no image URL was returned.");
      }
    } catch (err) {
      errorToastify(err?.response?.data?.message || "IAP Image upload failed");
    }
  };

  const fileUploadIapImage = async (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    iapFieldSetterRef.current = setFieldValue;
    setIapCropState({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name || "iap-image.jpg",
    });
    event.target.value = "";
  };

  const triggerLogoInput = () => {
    logoInputRef.current?.click();
  };

  const triggerBannerInput = () => {
    bannerInputRef.current?.click();
  };

  const triggerIapInput = () => {
    iapInputRef.current?.click();
  };

  return (
    <div id="main" className="main">
      <div className="pagetitle">
        <h1>Edit Challenge Pack</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/items">Challenge Packs</Link>
            </li>
            <li className="breadcrumb-item active">Edit</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-md-8">
              <Formik
                enableReinitialize
                initialValues={packageData}
                validationSchema={
                  submitMode === "complete" ? itemGroupSchema : null
                }
                validateOnChange={submitMode === "complete"}
                validateOnBlur={submitMode === "complete"}
                onSubmit={(vals) => onSubmitHandler(vals, submitMode)}
              >
                {({
                  values,
                  setFieldValue,
                  submitForm,
                  setErrors,
                  setTouched,
                }) => {
                  const moveItem = (fromIndex, toIndex) => {
                    const updatedItems = [...values.items];
                    const [moved] = updatedItems.splice(fromIndex, 1);
                    updatedItems.splice(toIndex, 0, moved);

                    setFieldValue("items", updatedItems);

                    const newOrder = updatedItems.map((item) => item.id);

                    clearTimeout(orderTimerRef.current);
                    orderTimerRef.current = setTimeout(() => {
                      dispatch(
                        updateItemOrder({
                          groupId: values.id,
                          itemOrder: newOrder,
                        }),
                      );
                    }, 300);
                  };

                  /** ✅ IMPORTANT FIX:
                   *  Always rebuild pool from ALL selected categories so old selected subs still show,
                   *  and submit is forced using submitForm()
                   */
                  const toggleCategory = async (catId) => {
                    const current = Array.isArray(values.categoryIds)
                      ? values.categoryIds
                      : [];
                    const idNum = Number(catId);
                    const exists = current.includes(idNum);

                    const next = exists
                      ? current.filter((x) => x !== idNum)
                      : [...current, idNum];

                    setFieldValue("categoryIds", next);
                    setFieldValue("category", next[0] ? String(next[0]) : "");

                    setSubPool([]);
                    if (next.length) {
                      await fetchAndMergeSubcategories(next);
                    }
                  };

                  const toggleSubCategory = (subId) => {
                    const current = Array.isArray(values.subCategoryIds)
                      ? values.subCategoryIds
                      : [];
                    const idNum = Number(subId);
                    const exists = current.includes(idNum);

                    const next = exists
                      ? current.filter((x) => x !== idNum)
                      : [...current, idNum];

                    setFieldValue("subCategoryIds", next);
                    setFieldValue(
                      "subCategory",
                      next[0] ? String(next[0]) : "",
                    );
                  };

                  const finalSubCategoryOptions = buildSubOptionsWithSelected(
                    subCategoryOptions,
                    values.subCategoryIds,
                  );

                  return (
                    <Form
                      onSubmit={(e) => {
                        // extra safety: do not let any parent/drag layer block it
                        e.stopPropagation();
                      }}
                    >
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <LevelReq htmlFor="name" className="form-label fs-7">
                            Challenge Pack Name
                          </LevelReq>
                          <Field
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                          />
                          <InputError name="name" />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <LevelReq
                            htmlFor="description"
                            className="form-label fs-7"
                          >
                            Challenge Pack Description
                          </LevelReq>
                          <Field
                            as="textarea"
                            id="description"
                            name="description"
                            className="form-control"
                            rows="4"
                          />
                          <InputError name="description" />
                        </div>
                      </div>

                      <div className="row mb-1">
                        <MultiSelectDropdown
                          label="Category"
                          placeholder="Search category..."
                          options={categoryOptions}
                          selectedIds={
                            Array.isArray(values.categoryIds)
                              ? values.categoryIds
                              : []
                          }
                          onToggle={toggleCategory}
                          onDone={() => {}}
                        />

                        <MultiSelectDropdown
                          label="Sub-Category"
                          placeholder="Search sub-category..."
                          options={finalSubCategoryOptions}
                          selectedIds={
                            Array.isArray(values.subCategoryIds)
                              ? values.subCategoryIds
                              : []
                          }
                          onToggle={toggleSubCategory}
                          onDone={() => {}}
                          disabled={
                            !Array.isArray(values.categoryIds) ||
                            values.categoryIds.length === 0
                          }
                        />
                      </div>

                      <Field type="hidden" id="category" name="category" />
                      <Field
                        type="hidden"
                        id="subCategory"
                        name="subCategory"
                      />

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <label htmlFor="price" className="form-label fs-7">
                            Price
                          </label>
                          <Field
                            type="text"
                            className="form-control"
                            id="price"
                            name="price"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                setFieldValue("price", value);
                              }
                            }}
                          />
                          <InputError name="price" />
                        </div>
                      </div>

                      {/* Logo + Banner */}
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label
                            htmlFor="package_logo"
                            className="form-label fs-7"
                          >
                            Logo
                          </label>
                          <br />
                          {logo ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={logo}
                                alt="logo_group"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setLogo("");
                                  setFieldValue("logo", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={logoInputRef}
                                id="package_logo"
                                name="package_logo"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadLogo(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Add Logo"
                                onClick={triggerLogoInput}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}
                          <InputError name="logo" />
                          <IapImageCropperModal
                            isOpen={logoCropState.isOpen}
                            imageSrc={logoCropState.imageSrc}
                            onCancel={closeLogoCropper}
                            onConfirm={uploadLogoImageBlob}
                            title="Crop Logo Image"
                            targetWidth={1}
                            targetHeight={1}
                            useSourceDimensions
                          />
                        </div>

                        <div className="col-md-6">
                          <label
                            htmlFor="banner_img"
                            className="form-label fs-7"
                          >
                            Banner Image (Aspect Ratio :- <b>16:9</b>)
                          </label>
                          <br />
                          {bannerImg ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={bannerImg}
                                alt="Banner_group"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setBannerImg("");
                                  setFieldValue("bannerImg", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={bannerInputRef}
                                id="banner_img"
                                name="banner_img"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadBanner(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Add Banner Image"
                                onClick={triggerBannerInput}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}
                          <InputError name="bannerImg" />
                          <IapImageCropperModal
                            isOpen={bannerCropState.isOpen}
                            imageSrc={bannerCropState.imageSrc}
                            onCancel={closeBannerCropper}
                            onConfirm={uploadBannerImageBlob}
                            title="Crop Banner Image"
                            targetWidth={16}
                            targetHeight={9}
                            useSourceDimensions
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="iap_img" className="form-label fs-7">
                            IAP Image (Aspect Ratio: 1260 × 2736)
                          </label>
                          <br />

                          {iapImage ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100px",
                                height: "100px",
                              }}
                            >
                              <img
                                src={iapImage}
                                alt="IAP"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setIapImage("");
                                  setFieldValue("iapimage", "");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  backgroundColor: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={iapInputRef}
                                id="iap_img"
                                name="iap_img"
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  fileUploadIapImage(event, setFieldValue)
                                }
                                style={{ display: "none" }}
                              />
                              <Button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={triggerIapInput}
                              >
                                <i className="fa-solid fa-upload"></i>
                              </Button>
                            </>
                          )}

                          <InputError name="iapimage" />
                          <IapImageCropperModal
                            isOpen={iapCropState.isOpen}
                            imageSrc={iapCropState.imageSrc}
                            onCancel={closeIapCropper}
                            onConfirm={uploadIapImageBlob}
                            title="Crop IAP Image"
                          />
                        </div>
                      </div>

                      <Field type="hidden" id="id" name="id" />
                      <hr />

                      {/* Items */}
                      <FieldArray name="items">
                        {({ remove, push }) => (
                          <div>
                            {values.items.map((item, index) => (
                              <DraggableItemCard
                                key={item.id || item._key || index}
                                index={index}
                                total={values.items.length}
                                item={item}
                                moveItem={moveItem}
                              >
                                <div className="card shadow-sm p-3">
                                  <div className="row">
                                    <div className="col-md-12 d-flex justify-content-between align-items-center">
                                      <h5 className="mb-2">Challenge {index + 1}</h5>
                                      {values.items.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setDeleteItems((prev) => [
                                              ...prev,
                                              item.id,
                                            ]);
                                            remove(index);
                                          }}
                                          className="btn btn-sm btn-outline-danger"
                                        >
                                          <i className="fa-solid fa-xmark"></i>
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  <div className="mb-2">
                                    <LevelReq className="form-label fs-7">
                                      Challenge Name
                                    </LevelReq>
                                    <Field
                                      name={`items.${index}.item_name`}
                                      className="form-control"
                                    />
                                  </div>

                                  <div className="mb-2">
                                    <label className="form-label fs-7">
                                      Challenge Description (optional)
                                    </label>
                                    <Field
                                      name={`items.${index}.item_desc`}
                                      className="form-control"
                                      as="textarea"
                                      rows={3}
                                    />
                                  </div>

                                  <div className="mb-2">
                                    <LevelReq className="form-label fs-7">
                                      Challenge Image (Aspect Ratio :- <b>1:1</b>)
                                    </LevelReq>
                                    <br />
                                    <input
                                      id={`item_img_${index}`}
                                      name={`items.${index}.item_img`}
                                      type="file"
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(event) =>
                                        fileUploadItem(
                                          event,
                                          index,
                                          setFieldValue,
                                        )
                                      }
                                    />

                                    {item.item_img ? (
                                      <div
                                        style={{
                                          position: "relative",
                                          width: "100px",
                                          height: "100px",
                                        }}
                                      >
                                        <img
                                          src={item.item_img}
                                          alt="Challenge"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setFieldValue(
                                              `items.${index}.item_img`,
                                              "",
                                            )
                                          }
                                          style={{
                                            position: "absolute",
                                            top: "-10px",
                                            right: "-10px",
                                            backgroundColor: "red",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            width: "20px",
                                            height: "20px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          &times;
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          document
                                            .getElementById(`item_img_${index}`)
                                            ?.click()
                                        }
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        <i className="fa-solid fa-upload"></i>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </DraggableItemCard>
                            ))}

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2 mb-3"
                              onClick={() =>
                                push({
                                  item_name: "",
                                  item_desc: "",
                                  item_img: "",
                                  position: values.items.length,
                                })
                              }
                            >
                              Add Challenge
                            </button>
                          </div>
                        )}
                      </FieldArray>

                      <IapImageCropperModal
                        isOpen={itemCropState.isOpen}
                        imageSrc={itemCropState.imageSrc}
                        onCancel={closeItemCropper}
                        onConfirm={uploadItemImageBlob}
                        title="Crop Challenge Image"
                        targetWidth={1000}
                        targetHeight={1000}
                        useSourceDimensions
                      />

                      <div className="col-12 mb-3 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-50"
                          disabled={isBtnLoad}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            flushSync(() => {
                              setSubmitMode("draft");
                              setErrors({});
                              setTouched({});
                            });
                            submitForm();
                          }}
                        >
                          {isBtnLoad && submitMode === "draft" ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            <>Save as Draft</>
                          )}
                        </button>
                        {user?.roleid === "1" && (
                          <button
                            type="button"
                            className="btn btn-primary w-50"
                            disabled={isBtnLoad}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                await itemGroupSchema.validate(values, {
                                  abortEarly: false,
                                });
                              } catch (err) {
                                let formErrors = {};
                                if (Array.isArray(err.inner) && err.inner.length) {
                                  err.inner.forEach((validationErr) => {
                                    if (validationErr.path) {
                                      formErrors = setIn(formErrors,
                                        validationErr.path,
                                        validationErr.message,
                                      );
                                    }
                                  });
                                } else if (err.message) {
                                  formErrors.form = err.message;
                                }
                                setErrors(formErrors);
                                setTouched(
                                  buildItemGroupCompleteTouched(values),
                                  false,
                                );
                                errorToastify(
                                  firstFormikErrorMessage(formErrors) ||
                                    "Please complete all required fields.",
                                );
                                return;
                              }
                              flushSync(() => setSubmitMode("complete"));
                              setTouched(
                                buildItemGroupCompleteTouched(values),
                                false,
                              );
                              submitForm();
                            }}
                          >
                            {isBtnLoad && submitMode === "complete" ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Submitting...
                              </>
                            ) : (
                              <>Complete</>
                            )}
                          </button>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>

            <div className="col-md-4"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ItemGroupUpdatePage;
